import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { TodoCounts } from "@vereinfach/types";
import { api } from "../lib/api";
import { useCurrentTenant } from "../lib/currentTenant";
import { capLabel, formatCHF, formatDate } from "../lib/format";

type Dashboard = {
  active_members: number;
  open_count: number;
  open_rappen: number;
  revenue_month_rappen: number;
  todo: TodoCounts;
  upcoming: {
    subscription_id: string;
    member_name: string;
    current_end: number;
    package_name: string;
  }[];
};

type TodoItem = { key: string; to: "/invoices" | "/packages"; text: string };

function todoItems(t: TodoCounts): TodoItem[] {
  const items: TodoItem[] = [];
  if (t.drafts > 0) {
    items.push({
      key: "drafts",
      to: "/invoices",
      text:
        t.drafts === 1
          ? `1 Rechnungsentwurf zu prüfen (${formatCHF(t.drafts_total_rappen)})`
          : `${t.drafts} Rechnungsentwürfe zu prüfen (${formatCHF(t.drafts_total_rappen)})`,
    });
  }
  if (t.overdue > 0) {
    items.push({
      key: "overdue",
      to: "/invoices",
      text: t.overdue === 1 ? "1 Rechnung überfällig" : `${t.overdue} Rechnungen überfällig`,
    });
  }
  if (t.expiring_subscriptions > 0) {
    items.push({
      key: "expiring",
      to: "/packages",
      text:
        t.expiring_subscriptions === 1
          ? "1 Beitrag läuft aus"
          : `${t.expiring_subscriptions} Beiträge laufen aus`,
    });
  }
  if (t.cancelled_with_money > 0) {
    items.push({
      key: "cancelled",
      to: "/invoices",
      text:
        t.cancelled_with_money === 1
          ? "1 stornierte Rechnung mit offener Zahlung"
          : `${t.cancelled_with_money} stornierte Rechnungen mit offener Zahlung`,
    });
  }
  if (t.stale_issued > 0) {
    items.push({
      key: "stale",
      to: "/invoices",
      text:
        t.stale_issued === 1
          ? "1 Rechnung besteht für pausierte/auslaufende Beiträge"
          : `${t.stale_issued} Rechnungen bestehen für pausierte/auslaufende Beiträge`,
    });
  }
  return items;
}

export function OverviewPage({ tenantId }: { tenantId: string }) {
  const tenant = useCurrentTenant();
  const [data, setData] = useState<Dashboard | null>(null);

  useEffect(() => {
    setData(null);
    api<Dashboard>(`/tenants/${tenantId}/dashboard`).then(setData);
  }, [tenantId]);

  if (!data) return <p className="text-slate-400">Lädt…</p>;

  const items = todoItems(data.todo);

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold tracking-tight">Übersicht</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Aktive Mitglieder" value={String(data.active_members)} />
        <Kpi
          label="Offene Forderungen"
          value={String(data.open_count)}
          sub={formatCHF(data.open_rappen)}
        />
        <Kpi label="Einnahmen diesen Monat" value={formatCHF(data.revenue_month_rappen)} />
      </div>

      <MemberCapCard count={data.active_members} cap={tenant.entitlement.cap} />

      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Zu erledigen
        </h3>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
            <span className="font-medium text-slate-700">Alles erledigt ✓</span> Vereinfach erstellt
            Rechnungsentwürfe automatisch 30 Tage vor jeder Verlängerung.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            {items.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm hover:bg-slate-50 group"
              >
                <span className="font-medium">{item.text}</span>
                <span className="text-slate-300 group-hover:text-slate-500">›</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Nächste Verlängerungen
        </h3>
        {data.upcoming.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-slate-500 text-sm">
            Keine anstehenden Verlängerungen.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            {data.upcoming.map((u) => (
              <div
                key={u.subscription_id}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.member_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.package_name}</div>
                </div>
                <div className="text-xs text-slate-500 text-right shrink-0">
                  verlängert am{" "}
                  <span className="font-medium text-slate-700">{formatDate(u.current_end)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 p-5">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

/** Member-cap usage with an upgrade nudge when near/over the limit. Hidden when unlimited. */
function MemberCapCard({ count, cap }: { count: number; cap: number | null }) {
  if (cap == null) return null;
  const pct = Math.min(100, Math.round((count / Math.max(1, cap)) * 100));
  const near = pct >= 80;
  const over = count > cap;
  return (
    <div
      className={
        "rounded-xl border p-5 " + (near ? "border-amber-200 bg-amber-50/60" : "border-slate-200")
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Mitglieder-Limit
          </div>
          <div className="mt-1 text-lg font-semibold">
            {count} / {capLabel(cap)}
          </div>
        </div>
        {near && (
          <Link
            to="/settings/abo"
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            {over ? "Limit überschritten — upgraden" : "Plan upgraden"}
          </Link>
        )}
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={
            "h-full " + (pct >= 90 ? "bg-red-500" : near ? "bg-amber-500" : "bg-slate-900")
          }
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
